import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { PluginConfigProps } from '../../types/PluginInterface';
import { EventBookingConfig, EventType } from './types';
import { useFileManager } from '../../hooks/useFileManager';
import { usePaymentApiKeys } from '../../hooks/usePaymentApiKeys';
import { useGoogleMapsApiKey } from '../../hooks/useGoogleMapsApiKey';
import AutoTranslateText from '../../components/AutoTranslateText';
import CountrySelect from '../../components/Common/CountrySelect';
import { api } from '../../services/api';
import PayPalOrdersPanel from '../../components/PayPalOrdersPanel';
import StripeOrdersPanel from '../../components/StripeOrdersPanel';
import { EventBookingRenderer } from './EventBookingRenderer';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../../hooks/useTinyMceApiKey';

function genId() {
  return 'rule-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

function slugify(v: string) {
  return v.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

const minutesBetween = (a?: string, b?: string) => {
  if (!a || !b || !/^\d{2}:\d{2}$/.test(a) || !/^\d{2}:\d{2}$/.test(b)) return undefined;
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  const diff = (bh*60+bm) - (ah*60+am);
  return diff > 0 ? diff : undefined;
};

const toLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (key: string) => {
  const parts = key.split('-').map(Number);
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const extractCheckInToken = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    const token = url.searchParams.get('token');
    return token ? token : trimmed;
  } catch {
    return trimmed;
  }
};

const buildCheckInUrl = (slug: string, token?: string) => {
  if (!slug) return '';
  const base = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
  const path = `/event-booking/check-in/${encodeURIComponent(slug)}`;
  if (!base) return token ? `${path}?token=${encodeURIComponent(token)}` : path;
  return token ? `${base}${path}?token=${encodeURIComponent(token)}` : `${base}${path}`;
};

const ReservationCalendar: React.FC<{
  selectedDate?: string | null;
  bookedDates: Set<string>;
  onSelect: (dateKey: string) => void;
}> = ({ selectedDate, bookedDates, onSelect }) => {
  const [cursor, setCursor] = useState(() => {
    if (selectedDate) {
      const parsed = parseDateKey(selectedDate);
      return parsed || new Date();
    }
    return new Date();
  });

  useEffect(() => {
    if (!selectedDate) return;
    const parsed = parseDateKey(selectedDate);
    if (parsed) setCursor(parsed);
  }, [selectedDate]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const startDay = (firstDay + 6) % 7;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const blanks = Array.from({ length: startDay });
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="card border-light bg-white">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCursor(new Date(year, month - 1, 1))}>
            ◀
          </button>
          <div className="fw-bold text-dark">{monthNames[month]} {year}</div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCursor(new Date(year, month + 1, 1))}>
            ▶
          </button>
        </div>
        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {['L','M','M','J','V','S','D'].map((label) => (
            <div key={label} className="text-muted text-center small fw-semibold">{label}</div>
          ))}
        </div>
        <div className="d-grid mt-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {blanks.map((_, idx) => (
            <div key={`blank-${idx}`} style={{ height: 32 }} />
          ))}
          {days.map((day) => {
            const date = new Date(year, month, day);
            const key = toLocalDateKey(date);
            const hasBooking = bookedDates.has(key);
            const isSelected = selectedDate === key;
            return (
              <button
                key={key}
                type="button"
                className={`btn btn-sm ${hasBooking ? 'btn-outline-success' : 'btn-light'} ${isSelected ? 'fw-bold' : ''}`}
                style={{
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  padding: 0,
                  margin: '0 auto'
                }}
                onClick={() => hasBooking && onSelect(key)}
                disabled={!hasBooking}
                title={hasBooking ? 'Réservations' : 'Aucune réservation'}
              >
                {day}
              </button>
            );
          })}
        </div>
        <div className="mt-3 d-flex gap-3 justify-content-center small text-muted">
          <span><span className="badge bg-success me-1">●</span>Réservé</span>
          <span><span className="badge bg-light text-dark me-1">●</span>Libre</span>
        </div>
      </div>
    </div>
  );
};

const QrCodeImage: React.FC<{ value: string; size?: number }> = ({ value, size = 140 }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    if (!value) {
      setDataUrl(null);
      return;
    }
    QRCode.toDataURL(value, { margin: 1, width: size })
      .then((url) => {
        if (mounted) setDataUrl(url);
      })
      .catch(() => {
        if (mounted) setDataUrl(null);
      });
    return () => {
      mounted = false;
    };
  }, [value, size]);

  if (!value) return <div className="text-muted small">QR indisponible</div>;
  if (!dataUrl) return <div className="text-muted small">Génération du QR…</div>;
  return <img src={dataUrl} alt="QR code" style={{ width: size, height: size }} />;
};

export const EventBookingConfigComponent: React.FC<PluginConfigProps> = ({ 
  config, 
  onConfigChange 
}) => {
  const currentConfig = config as EventBookingConfig;
  const [local, setLocal] = useState(currentConfig);
  const [tab, setTab] = useState<'info' | 'pricing' | 'schedule' | 'address' | 'appearance' | 'legal' | 'dashboard' | 'payment' | 'paypal' | 'stripe'>('info');
  const [reservations, setReservations] = useState<any[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [reservationsError, setReservationsError] = useState<string | null>(null);
  const [selectedReservationDate, setSelectedReservationDate] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | string | null>(null);
  const [checkInLoadingId, setCheckInLoadingId] = useState<number | string | null>(null);
  const [qrExpanded, setQrExpanded] = useState<Record<string, boolean>>({});
  const [scanValue, setScanValue] = useState('');
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [checkInLinkCopied, setCheckInLinkCopied] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const scannerVideoRef = useRef<HTMLVideoElement | null>(null);
  const scannerStreamRef = useRef<MediaStream | null>(null);
  const scannerIntervalRef = useRef<number | null>(null);
  const scannerActiveRef = useRef(false);
  const addressHydratedRef = useRef<string | null>(null);
  const { openFileManager } = useFileManager();
  const { isLoading, error, hasStripeKey, hasPaypalKey } = usePaymentApiKeys();
  const { isLoading: gmLoading, error: gmError, hasApiKey: hasGMapKey } = useGoogleMapsApiKey();
  const { apiKey: tinymceApiKey, isLoading: tinymceLoading } = useTinyMceApiKey();

  // Désactivé pour éviter la duplication
  const inlinePreview = false;

  useEffect(() => {
    setLocal(currentConfig);
  }, [currentConfig]);

  const update = useCallback((patch: Partial<EventBookingConfig>) => {
    setLocal((prev) => {
      const next = { ...prev, ...patch };
      onConfigChange(next);
      return next;
    });
  }, [onConfigChange]);

  useEffect(() => {
    if (!local.slug) return;
    if (addressHydratedRef.current === local.slug) return;
    const hasAddress = !!(local.addressLine1 || local.city || local.postalCode || local.country);
    if (hasAddress) {
      addressHydratedRef.current = local.slug;
      return;
    }
    let cancelled = false;
    const loadAddress = async () => {
      try {
        const resp: any = await api.get(`/events/${encodeURIComponent(local.slug)}`);
        if (cancelled || !resp?.success || !resp?.event) return;
        const event = resp.event;
        const patch: Partial<EventBookingConfig> = {};
        if (!local.addressLine1 && event.addressLine1) patch.addressLine1 = event.addressLine1;
        if (!local.city && event.city) patch.city = event.city;
        if (!local.postalCode && event.postalCode) patch.postalCode = event.postalCode;
        if (!local.country && event.country) patch.country = event.country;
        if ((!local.ticketCategories || local.ticketCategories.length === 0) && Array.isArray(event.ticketCategories)) {
          patch.ticketCategories = event.ticketCategories;
        }
        if ((!local.coupons || local.coupons.length === 0) && Array.isArray(event.coupons)) {
          patch.coupons = event.coupons;
        }
        if (Object.keys(patch).length) update(patch);
      } catch {
        // ignore
      } finally {
        if (!cancelled) addressHydratedRef.current = local.slug;
      }
    };
    loadAddress();
    return () => {
      cancelled = true;
    };
  }, [local.slug, local.addressLine1, local.city, local.postalCode, local.country, update]);

  // ✅ Associer l'utilisateur créateur au plugin
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || currentConfig.userId) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      if (ownerId) {
        onConfigChange({
          ...currentConfig,
          userId: String(ownerId),
          ownerId: String(ownerId)
        });
      }
    } catch {
      console.warn("Impossible de récupérer l'ID utilisateur pour le plugin.");
    }
  }, [currentConfig.userId, onConfigChange]);

  const loadReservations = async () => {
    if (!local.slug) return;
    setReservationsLoading(true);
    setReservationsError(null);
    try {
      const resp = await api.get<any>(`/events/${encodeURIComponent(local.slug)}/reservations`);
      if (resp?.success) {
        setReservations(resp.reservations || []);
      } else {
        setReservations([]);
        setReservationsError(resp?.message || 'Impossible de charger les réservations');
      }
    } catch (err: any) {
      setReservations([]);
      setReservationsError(err?.message || 'Erreur lors du chargement des réservations');
    } finally {
      setReservationsLoading(false);
    }
  };

  const cancelReservation = async (reservationId: number | string) => {
    if (!local.slug) return;
    if (!window.confirm('Annuler cette réservation ?')) return;
    setCancellingId(reservationId);
    try {
      const resp: any = await api.post(`/events/${encodeURIComponent(local.slug)}/reservations/${reservationId}/cancel`, {});
      if (!resp?.success) {
        alert(resp?.message || 'Impossible d’annuler la réservation');
        return;
      }
      await loadReservations();
    } catch (err: any) {
      alert(err?.message || 'Erreur lors de l’annulation');
    } finally {
      setCancellingId(null);
    }
  };

  const stopScanner = useCallback(() => {
    scannerActiveRef.current = false;
    if (scannerIntervalRef.current) {
      window.clearInterval(scannerIntervalRef.current);
      scannerIntervalRef.current = null;
    }
    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }
    if (scannerVideoRef.current) {
      scannerVideoRef.current.pause();
      scannerVideoRef.current.srcObject = null;
    }
    setScannerActive(false);
  }, []);

  useEffect(() => {
    return () => stopScanner();
  }, [stopScanner]);

  const checkInReservation = useCallback(async (payload: { token?: string; reservationId?: number | string }) => {
    if (!local.slug) return;
    setScanStatus(null);
    setScanError(null);
    const resolvedToken = payload.token ? extractCheckInToken(payload.token) : '';
    if (!payload.reservationId && !resolvedToken) {
      setScanError('Code de présence invalide.');
      return;
    }
    try {
      const resp: any = await api.post(`/events/${encodeURIComponent(local.slug)}/check-in`, payload.reservationId ? payload : { token: resolvedToken });
      if (resp?.success) {
        const name = resp?.reservation?.name || resp?.reservation?.email || 'Réservation';
        setScanStatus(`✅ Présence confirmée: ${name}`);
        await loadReservations();
      } else {
        setScanError(resp?.message || 'Impossible de valider la présence');
      }
    } catch (err: any) {
      setScanError(err?.message || 'Erreur lors du check-in');
    }
  }, [local.slug]);

  const startScanner = useCallback(async () => {
    if (!('BarcodeDetector' in window)) {
      setScanError('Scanner QR non supporté par ce navigateur.');
      return;
    }
    setScanError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      scannerStreamRef.current = stream;
      if (scannerVideoRef.current) {
        scannerVideoRef.current.srcObject = stream;
        await scannerVideoRef.current.play();
      }
      scannerActiveRef.current = true;
      setScannerActive(true);
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
      scannerIntervalRef.current = window.setInterval(async () => {
        if (!scannerActiveRef.current || !scannerVideoRef.current) return;
        try {
          const codes = await detector.detect(scannerVideoRef.current);
          if (codes && codes.length) {
            const value = codes[0]?.rawValue;
            if (value) {
              stopScanner();
              setScanValue(value);
              await checkInReservation({ token: value });
            }
          }
        } catch {
          // ignore detect errors
        }
      }, 500);
    } catch (err: any) {
      setScanError(err?.message || 'Impossible d’accéder à la caméra.');
      stopScanner();
    }
  }, [checkInReservation, stopScanner]);

  useEffect(() => {
    if (tab !== 'dashboard') return;
    loadReservations();
  }, [tab, local.slug]);

  const reservationsByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (reservations || []).forEach((reservation) => {
      const start = reservation?.occurrence?.startsAt ? new Date(reservation.occurrence.startsAt) : null;
      if (!start || Number.isNaN(start.getTime())) return;
      const key = toLocalDateKey(start);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(reservation);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const da = new Date(a?.occurrence?.startsAt || 0).getTime();
        const db = new Date(b?.occurrence?.startsAt || 0).getTime();
        return da - db;
      });
    });
    return grouped;
  }, [reservations]);

  const bookedDates = useMemo(() => new Set(Object.keys(reservationsByDate)), [reservationsByDate]);

  useEffect(() => {
    if (tab !== 'dashboard') return;
    if (selectedReservationDate && reservationsByDate[selectedReservationDate]) return;
    const first = Object.keys(reservationsByDate).sort()[0];
    setSelectedReservationDate(first || null);
  }, [tab, reservationsByDate, selectedReservationDate]);

  const attendanceStats = useMemo(() => {
    const paid = (reservations || []).filter((r) => r.status === 'PAID');
    const totalPaid = paid.reduce((sum, r) => sum + (r.qty || 1), 0);
    const checkedIn = paid.reduce((sum, r) => sum + (r.checkedInAt ? (r.qty || 1) : 0), 0);
    const totalReservations = (reservations || []).reduce((sum, r) => sum + (r.qty || 1), 0);
    const totalRevenue = paid.reduce((sum, r) => sum + (r.amountCents || 0), 0) / 100;
    const byCat: Record<string, { name: string; qty: number; revenue: number }> = {};
    (reservations || []).forEach((r) => {
      if (r.status !== 'PAID') return;
      const catId = String(r.ticketCategoryId ?? 'default');
      const catName = r.ticketCategory?.name ?? 'Standard';
      if (!byCat[catId]) byCat[catId] = { name: catName, qty: 0, revenue: 0 };
      byCat[catId].qty += r.qty || 1;
      byCat[catId].revenue += (r.amountCents || 0) / 100;
    });
    return { totalPaid, checkedIn, totalReservations, totalRevenue, byCat };
  }, [reservations]);

  const checkInBaseUrl = useMemo(() => (local.slug ? buildCheckInUrl(local.slug) : ''), [local.slug]);

  const renderStyleControls = (prefix: string, label: string, hideColor: boolean = false) => {
    const cfg = local as any;
    const updateField = (key: string, val: any) => update({ [key]: val } as any);
    const p = prefix;

    return (
      <div className="card mb-3 border-light bg-light">
        <div className="card-body">
          <h6 className="card-title text-muted mb-3">{label}</h6>
          <div className="row g-2 align-items-end mb-2">
            {!hideColor && (
              <div className="col-md-4">
                <label className="form-label small">Couleur Texte</label>
                <input
                  type="color"
                  className="form-control form-control-color w-100"
                  value={cfg[`${p}Color`] || '#000000'}
                  onChange={(e) => updateField(`${p}Color`, e.target.value)}
                />
              </div>
            )}
            <div className={hideColor ? "col-md-6" : "col-md-4"}>
              <label className="form-label small">Taille</label>
              <select
                className="form-select"
                value={cfg[`${p}FontSize`] || '1rem'}
                onChange={(e) => updateField(`${p}FontSize`, e.target.value)}
              >
                <option value="0.8rem">Petit</option>
                <option value="0.9rem">Standard (0.9)</option>
                <option value="1rem">Normal (1.0)</option>
                <option value="1.1rem">Moyen (1.1)</option>
                <option value="1.25rem">Grand (1.25)</option>
                <option value="1.5rem">Très Grand (1.5)</option>
                <option value="2rem">XXL (2.0)</option>
              </select>
            </div>
            <div className={hideColor ? "col-md-6" : "col-md-4"}>
              <label className="form-label small">Police</label>
              <select
                className="form-select"
                value={cfg[`${p}FontFamily`] || 'inherit'}
                onChange={(e) => updateField(`${p}FontFamily`, e.target.value)}
              >
                <option value="inherit">Par défaut</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="Verdana, sans-serif">Verdana</option>
              </select>
            </div>
          </div>
          <div className="btn-group w-100">
            <button
              className={`btn btn-sm ${cfg[`${p}Bold`] ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => updateField(`${p}Bold`, !cfg[`${p}Bold`])}
              type="button"
            >
              Gras
            </button>
            <button
              className={`btn btn-sm ${cfg[`${p}Italic`] ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => updateField(`${p}Italic`, !cfg[`${p}Italic`])}
              type="button"
            >
              Italique
            </button>
            <button
              className={`btn btn-sm ${cfg[`${p}Underline`] ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => updateField(`${p}Underline`, !cfg[`${p}Underline`])}
              type="button"
            >
              Souligné
            </button>
          </div>
        </div>
      </div>
    );
  };

  const onTitleChange = (val: string) => {
    const auto = !local.slug || local.slug === slugify(local.title || '');
    update({ title: val, slug: auto ? slugify(val) : local.slug });
  };

  const openImageSelector = () => {
    openFileManager({
      onFileSelect: (f: { url: string }) => f?.url && update({ imageUrl: f.url }),
      type: 'image'
    });
  };

  const openVideoSelector = () => {
    openFileManager({
      onFileSelect: (f: { url: string }) => f?.url && update({ videoUrl: f.url }),
      type: 'video'
    });
  };

  // Règles appointment
  const addAppointmentRule = () => {
    const rules = local.appointmentRulesJSON || [];
    const newRule = {
      id: genId(),
      date: undefined,
      startDate: undefined,
      endDate: undefined,
      hoursStart: '09:00',
      hoursEnd: '18:00',
      slotDurationMin: 60
    };
    update({ appointmentRulesJSON: [...rules, newRule] });
  };

  const updateRule = (id: string, patch: any) => {
    const rules = (local.appointmentRulesJSON || []).map(r => {
      if (r.id !== id) return r;
      const next = { ...r, ...patch };
      // Si date unique + heures => suggère la durée uniquement si l'utilisateur
      // ne modifie pas slotDurationMin lui-même
      if (next.date && next.hoursStart && next.hoursEnd && !('slotDurationMin' in patch)) {
        const d = minutesBetween(next.hoursStart, next.hoursEnd);
        if (d) next.slotDurationMin = d;
      }
      return next;
    });
    update({ appointmentRulesJSON: rules });
  };

  const deleteRule = (id: string) => {
    update({
      appointmentRulesJSON: (local.appointmentRulesJSON || []).filter(r => r.id !== id)
    });
  };

  const updateTicketCategories = (next: any[]) => {
    update({ ticketCategories: next });
  };

  const addTicketCategory = () => {
    const current = local.ticketCategories || [];
    updateTicketCategories([
      ...current,
      {
        name: `Standard ${current.length + 1}`,
        priceCents: local.priceCents || 0,
        quota: local.defaultCapacity || 1,
        active: true,
        sortOrder: current.length
      }
    ]);
  };

  const updateTicketCategory = (index: number, patch: any) => {
    const current = [...(local.ticketCategories || [])];
    current[index] = { ...current[index], ...patch };
    updateTicketCategories(current);
  };

  const removeTicketCategory = (index: number) => {
    const current = [...(local.ticketCategories || [])];
    current.splice(index, 1);
    updateTicketCategories(current);
  };

  const updateCoupons = (next: any[]) => {
    update({ coupons: next });
  };

  const addCoupon = () => {
    const current = local.coupons || [];
    updateCoupons([
      ...current,
      {
        code: '',
        type: 'PERCENT',
        percentOff: 10,
        amountCents: 0,
        maxUses: undefined,
        active: true
      }
    ]);
  };

  const updateCoupon = (index: number, patch: any) => {
    const current = [...(local.coupons || [])];
    current[index] = { ...current[index], ...patch };
    updateCoupons(current);
  };

  const removeCoupon = (index: number) => {
    const current = [...(local.coupons || [])];
    current.splice(index, 1);
    updateCoupons(current);
  };

  if (isLoading) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border spinner-border-sm" />{' '}
        <AutoTranslateText sourceLang="fr">Chargement des clés API...</AutoTranslateText>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">Erreur: {error}</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        alignItems: 'flex-start',
        maxHeight: '78vh',
        overflow: 'hidden',
        flexWrap: 'wrap'
      }}
    >
      {inlinePreview && (
        <div
          style={{
            flex: '1 1 360px',
            maxWidth: 420,
            width: '100%',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start'
          }}
        >
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              background: '#ffffff',
              padding: 12,
              boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}
          >
            <div style={{ minWidth: 0, width: '100%' }}>
              {/* Aperçu isolé */}
              <EventBookingRenderer config={local as any} />
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          flex: '2 1 360px',
          minWidth: 280,
          overflowY: 'auto',
          paddingRight: 4,
          maxHeight: '78vh',
          display: 'grid',
          gap: 16
        }}
      >
        {/* ==== Contenu formulaire existant ==== */}
        {/* Alertes clés API */}
        {local.priceCents > 0 && !hasStripeKey && !hasPaypalKey && (
          <div className="alert alert-warning">
            <AutoTranslateText sourceLang="fr">
              ⚠️ Aucune clé Stripe ou PayPal configurée (paiement).
            </AutoTranslateText>
          </div>
        )}

        {/* Titre + Type */}
        <div>
          <label style={{ fontWeight: 600 }}>
            <AutoTranslateText sourceLang="fr">Titre</AutoTranslateText>
          </label>
          <input
            type="text"
            className="form-control"
            value={local.title}
            onChange={e => onTitleChange(e.target.value)}
            placeholder="Concert de jazz, Cours de yoga..."
          />
          {local.slug && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Slug: <code>{local.slug}</code>
            </div>
          )}
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
          {(['info', 'pricing', 'schedule', 'payment', 'appearance', 'address', 'legal', 'dashboard', 'paypal', 'stripe'] as const).filter(t => (t !== 'paypal' || hasPaypalKey) && (t !== 'stripe' || hasStripeKey)).map(t => (
            <button
              key={t}
              className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setTab(t as any)}
              type="button"
            >
              <AutoTranslateText sourceLang="fr">
                {t === 'info'
                  ? 'Infos'
                  : t === 'pricing'
                  ? 'Tarifs'
                  : t === 'schedule'
                  ? 'Horaires'
                  : t === 'payment'
                  ? 'Paiement'
                  : t === 'appearance'
                  ? 'Apparence'
                  : t === 'address'
                  ? 'Adresse'
                  : t === 'legal'
                  ? 'CGU/RGPD'
                  : t === 'dashboard'
                  ? 'Dashboard'
                  : t === 'paypal'
                  ? '🅿 PayPal'
                  : '💜 Stripe'}
              </AutoTranslateText>
            </button>
          ))}
        </div>

        {/* Onglet Info */}
        {tab === 'info' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label><AutoTranslateText sourceLang="fr">Description</AutoTranslateText></label>
              {tinymceLoading ? (
                <div>Chargement...</div>
              ) : !tinymceApiKey ? (
                <>
                  <div className="alert alert-warning py-2">Clé TinyMCE manquante</div>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={local.description || ''}
                    onChange={e => update({ description: e.target.value })}
                  />
                </>
              ) : (
                <Editor
                  apiKey={tinymceApiKey}
                  value={local.description || ''}
                  onEditorChange={(content) => update({ description: content })}
                  init={withTabLink({
                    height: 200,
                    menubar: false,
                    plugins: [
                      'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                      'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'wordcount'
                    ],
                    toolbar: 'undo redo | fontfamily fontsize | forecolor backcolor | formatselect | bold italic underline strikethrough | ' +
                      'alignleft aligncenter alignright alignjustify | ' +
                      'bullist numlist outdent indent | removeformat | ' +
                      'link image media | table | code fullscreen',
                    font_family_formats: 'Arial=arial,helvetica,sans-serif;Times New Roman=times new roman,times;Courier New=courier new,courier;Georgia=georgia,palatino;Verdana=verdana,geneva',
                    content_style: `
                      body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                      img[style*="float: left"] { margin: 0 12px 12px 0; }
                      img[style*="float: right"] { margin: 0 0 12px 12px; }
                    `,
                    language: 'fr',
                    branding: false,
                    allow_script_urls: true,
                    allow_html_data_urls: true,
                    allow_events: true,
                    extended_valid_elements: 'div[*],span[*],i[*],button[*]',
                    custom_elements: 'button,i',
                    valid_children: '+body[div|button]',
                    valid_elements: '*[*]',
                    paste_data_images: true,
                    paste_as_text: false,
                    paste_retain_style_properties: 'all'
                  })}
                />
              )}
            </div>

            {/* ✅ Image avec gestionnaire de fichiers */}
            <div>
              <label><AutoTranslateText sourceLang="fr">Image</AutoTranslateText></label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={local.imageUrl ? decodeURIComponent(local.imageUrl.split('/').pop() || '') : ''}
                  placeholder="Aucune image sélectionnée"
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={openImageSelector}
                >
                  📁
                </button>
                {local.imageUrl && (
                  <button
                    className="btn btn-outline-danger"
                    type="button"
                    onClick={() => update({ imageUrl: '' })}
                    title="Supprimer l'image"
                  >
                    ✕
                  </button>
                )}
              </div>
              {local.imageUrl && (
                <div className="mt-2">
                  <img
                    src={local.imageUrl}
                    alt="cover"
                    style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8 }}
                    className="img-thumbnail"
                  />
                </div>
              )}
            </div>

            {/* ✅ Vidéo */}
            <div>
              <label><AutoTranslateText sourceLang="fr">Vidéo (optionnel)</AutoTranslateText></label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={local.videoUrl || ''}
                  placeholder="URL vidéo"
                  onChange={e => update({ videoUrl: e.target.value })}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={openVideoSelector}
                >
                  🎥
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <label><AutoTranslateText sourceLang="fr">Prix</AutoTranslateText></label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  step="0.01"
                  value={(local.priceCents ?? 0) / 100}
                  onChange={e => {
                    const val = e.target.value;
                    const euros = val === '' ? 0 : parseFloat(val);
                    const cents = Math.round((isFinite(euros) ? euros : 0) * 100);
                    update({ priceCents: cents });
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label><AutoTranslateText sourceLang="fr">Devise</AutoTranslateText></label>
                <select
                  className="form-select"
                  value={local.currency}
                  onChange={e => update({ currency: e.target.value })}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div className="alert alert-info">
              <AutoTranslateText sourceLang="fr">
                La vue publique est désormais simplifiée : calendrier + créneaux + réservation.
              </AutoTranslateText>
            </div>
          </div>
        )}

        {tab === 'pricing' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="alert alert-info">
              <AutoTranslateText sourceLang="fr">
                Configurez des tarifs par catégorie (Standard, VIP, Enfant) et des coupons. Sans catégorie, le prix global s’applique.
              </AutoTranslateText>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Tarifs / Catégories</h6>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTicketCategory}>
                  + Ajouter un tarif
                </button>
              </div>
              {(local.ticketCategories || []).length === 0 && (
                <div className="text-muted small">Aucun tarif défini pour l’instant.</div>
              )}
              {(local.ticketCategories || []).map((cat, idx) => (
                <div key={String(cat.id || idx)} className="card border-light">
                  <div className="card-body" style={{ display: 'grid', gap: 10 }}>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="form-label small">Nom</label>
                        <input
                          type="text"
                          className="form-control"
                          value={cat.name || ''}
                          onChange={(e) => updateTicketCategory(idx, { name: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Prix</label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          step="0.01"
                          value={(cat.priceCents ?? 0) / 100}
                          onChange={(e) => {
                            const euros = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            updateTicketCategory(idx, { priceCents: Math.round((isFinite(euros) ? euros : 0) * 100) });
                          }}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Quota</label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          value={cat.quota ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateTicketCategory(idx, { quota: val === '' ? undefined : Math.max(0, Number(val)) });
                          }}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end gap-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={cat.active !== false}
                            onChange={(e) => updateTicketCategory(idx, { active: e.target.checked })}
                          />
                          <label className="form-check-label small">Actif</label>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeTicketCategory(idx)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Coupons / Codes promo</h6>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addCoupon}>
                  + Ajouter un coupon
                </button>
              </div>
              {(local.coupons || []).length === 0 && (
                <div className="text-muted small">Aucun coupon défini.</div>
              )}
              {(local.coupons || []).map((coupon, idx) => (
                <div key={String(coupon.id || idx)} className="card border-light">
                  <div className="card-body" style={{ display: 'grid', gap: 10 }}>
                    <div className="row g-2">
                      <div className="col-md-3">
                        <label className="form-label small">Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={coupon.code || ''}
                          onChange={(e) => updateCoupon(idx, { code: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">Type</label>
                        <select
                          className="form-select"
                          value={coupon.type || 'PERCENT'}
                          onChange={(e) => updateCoupon(idx, { type: e.target.value })}
                        >
                          <option value="PERCENT">%</option>
                          <option value="FIXED">Montant</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">
                          {coupon.type === 'FIXED' ? 'Montant (€)' : 'Pourcentage (%)'}
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          step={coupon.type === 'FIXED' ? '0.01' : '1'}
                          value={
                            coupon.type === 'FIXED'
                              ? (coupon.amountCents ?? 0) / 100
                              : (coupon.percentOff ?? 0)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (coupon.type === 'FIXED') {
                              const euros = raw === '' ? 0 : parseFloat(raw);
                              updateCoupon(idx, { amountCents: Math.round((isFinite(euros) ? euros : 0) * 100) });
                            } else {
                              updateCoupon(idx, { percentOff: Math.max(0, Math.min(100, Number(raw))) });
                            }
                          }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">Limite</label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          value={coupon.maxUses ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateCoupon(idx, { maxUses: val === '' ? undefined : Math.max(0, Number(val)) });
                          }}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end gap-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={coupon.active !== false}
                            onChange={(e) => updateCoupon(idx, { active: e.target.checked })}
                          />
                          <label className="form-check-label small">Actif</label>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="small text-muted">
                        {coupon.usedCount ? `Utilisé ${coupon.usedCount} fois` : 'Non utilisé'}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeCoupon(idx)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Onglet Schedule */}
        {tab === 'schedule' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label><AutoTranslateText sourceLang="fr">Jours ouvrés</AutoTranslateText></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['MON','TUE','WED','THU','FRI','SAT','SUN'].map(d => (
                <label key={d} style={{ fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={(local.businessDaysJSON || ['MON','TUE','WED','THU','FRI']).includes(d)}
                    onChange={e => {
                      const days = local.businessDaysJSON || [];
                      update({
                        businessDaysJSON: e.target.checked
                          ? [...days, d]
                          : days.filter(x => x !== d)
                      });
                    }}
                  /> {d}
                </label>
              ))}
            </div>

            {/* Règles spécifiques */}
            <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
              <label style={{ fontWeight: 600 }}>
                <AutoTranslateText sourceLang="fr">Règles spécifiques (dates / intervalles)</AutoTranslateText>
              </label>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 10px' }}>
                <AutoTranslateText sourceLang="fr">
                  Une date exacte prime sur un intervalle; sinon horaires globaux.
                </AutoTranslateText>
              </p>
              {(local.appointmentRulesJSON || []).map(r => (
                <div
                  key={r.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))',
                    gap: 8,
                    background: '#f8fafc',
                    padding: 8,
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    marginBottom: 6
                  }}
                >
                  <div>
                    <small>Date (unique)</small>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={r.date || ''}
                      onChange={e => updateRule(r.id, { date: e.target.value || undefined })}
                    />
                  </div>
                  <div>
                    <small>Début (intervalle)</small>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={r.startDate || ''}
                      onChange={e => updateRule(r.id, { startDate: e.target.value || undefined })}
                    />
                  </div>
                  <div>
                    <small>Fin (intervalle)</small>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={r.endDate || ''}
                      onChange={e => updateRule(r.id, { endDate: e.target.value || undefined })}
                    />
                  </div>
                  <div>
                    <small>Heure début</small>
                    <input
                      type="time"
                      className="form-control form-control-sm"
                      value={r.hoursStart}
                      onChange={e => updateRule(r.id, { hoursStart: e.target.value })}
                    />
                  </div>
                  <div>
                    <small>Heure fin</small>
                    <input
                      type="time"
                      className="form-control form-control-sm"
                      value={r.hoursEnd}
                      onChange={e => updateRule(r.id, { hoursEnd: e.target.value })}
                    />
                  </div>
                  <div>
                    <small>Durée (min)</small>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={5}
                      value={r.slotDurationMin || 0}
                      onChange={e => updateRule(r.id, { slotDurationMin: Math.max(1, Number(e.target.value) || 1) })}
                    />
                    {r.date && r.hoursStart && r.hoursEnd && (
                      <small className="text-muted">
                        Fenêtre : {minutesBetween(r.hoursStart, r.hoursEnd) ?? '?'} min
                      </small>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      className="btn btn-sm btn-outline-danger w-100"
                      type="button"
                      onClick={() => deleteRule(r.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={addAppointmentRule}
              >
                ➕ <AutoTranslateText sourceLang="fr">Ajouter une règle</AutoTranslateText>
              </button>
            </div>
          </div>
        )}

        {tab === 'appearance' && (
          <div className="animate-fade-in">
            <h6 className="border-bottom pb-2 mb-3 text-primary">Style général</h6>
            <div className="card mb-3 border-light bg-light">
              <div className="card-body">
                <h6 className="card-title text-muted mb-3">Couleur principale</h6>
                <input
                  type="color"
                  className="form-control form-control-color w-100"
                  value={local.primaryColor || '#4f46e5'}
                  onChange={(e) => update({ primaryColor: e.target.value })}
                />
              </div>
            </div>
            <div className="card mb-3 border-light bg-light">
              <div className="card-body">
                <h6 className="card-title text-muted mb-3">Couleur de fond</h6>
                <input
                  type="color"
                  className="form-control form-control-color w-100"
                  value={local.backgroundColor || '#ffffff'}
                  onChange={(e) => update({ backgroundColor: e.target.value })}
                />
              </div>
            </div>

            {renderStyleControls('title', 'Titre de la section')}
            {renderStyleControls('description', 'Description')}

            <div className="card mb-3 border-light bg-light">
              <div className="card-body">
                <h6 className="card-title text-muted mb-3">Bouton de réservation</h6>
                <div className="mb-3">
                  <label className="form-label small">Texte du bouton</label>
                  <input
                    type="text"
                    className="form-control"
                    value={local.actionButtonText || ''}
                    placeholder="Réserver"
                    onChange={(e) => update({ actionButtonText: e.target.value })}
                  />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small">Couleur fond</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      value={local.actionButtonBackgroundColor || local.primaryColor || '#4f46e5'}
                      onChange={(e) => update({ actionButtonBackgroundColor: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Couleur texte</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      value={local.actionButtonTextColor || '#ffffff'}
                      onChange={(e) => update({ actionButtonTextColor: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="p-0">
                {renderStyleControls('actionButton', 'Typographie bouton', true)}
              </div>
            </div>
          </div>
        )}

        {tab === 'legal' && (
          <div className="animate-fade-in">
            <div className="mb-4">
              <label className="form-label fw-bold">CGU / CGV</label>
              {tinymceLoading ? (
                <div>Chargement...</div>
              ) : !tinymceApiKey ? (
                <div className="alert alert-warning">Clé TinyMCE manquante</div>
              ) : (
                <Editor
                  apiKey={tinymceApiKey}
                  value={local.termsCgv || ''}
                  onEditorChange={(content) => update({ termsCgv: content })}
                  init={withTabLink({
                    height: 260,
                    menubar: false,
                    plugins: ['link', 'lists', 'code', 'autolink'],
                    toolbar: 'undo redo | bold italic underline | bullist numlist | link | code'
                  })}
                />
              )}
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">RGPD</label>
              {tinymceLoading ? (
                <div>Chargement...</div>
              ) : !tinymceApiKey ? (
                <div className="alert alert-warning">Clé TinyMCE manquante</div>
              ) : (
                <Editor
                  apiKey={tinymceApiKey}
                  value={local.termsRgpd || ''}
                  onEditorChange={(content) => update({ termsRgpd: content })}
                  init={withTabLink({
                    height: 260,
                    menubar: false,
                    plugins: ['link', 'lists', 'code', 'autolink'],
                    toolbar: 'undo redo | bold italic underline | bullist numlist | link | code'
                  })}
                />
              )}
            </div>
          </div>
        )}

        {tab === 'dashboard' && (
          <div className="animate-fade-in">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h6 className="m-0 fw-bold">Réservations</h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={loadReservations}
                disabled={reservationsLoading}
              >
                {reservationsLoading ? '...' : '↻ Recharger'}
              </button>
            </div>

            {reservationsLoading && (
              <div className="text-muted">Chargement des réservations…</div>
            )}
            {reservationsError && (
              <div className="alert alert-warning">{reservationsError}</div>
            )}

            {!reservationsLoading && !reservationsError && (
              <div className="d-grid gap-3">
                {/* KPI cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10 }}>
                  {[
                    { label: 'Places vendues', value: attendanceStats.totalPaid, color: '#4f46e5', bg: '#ede9fe' },
                    { label: 'Présents', value: attendanceStats.checkedIn, color: '#059669', bg: '#d1fae5' },
                    { label: 'Total réservations', value: attendanceStats.totalReservations, color: '#0284c7', bg: '#e0f2fe' },
                    { label: 'Revenus', value: `${attendanceStats.totalRevenue.toFixed(2)} ${local.currency || 'EUR'}`, color: '#b45309', bg: '#fef3c7' },
                  ].map(({ label, value, color, bg }) => (
                    <div key={label} style={{ background: bg, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                    </div>
                  ))}
                </div>
                {attendanceStats.totalPaid > 0 && (
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Taux de présence : <strong>{Math.round((attendanceStats.checkedIn / attendanceStats.totalPaid) * 100)}%</strong>
                    {Object.keys(attendanceStats.byCat).length > 1 && (
                      <span style={{ marginLeft: 12 }}>
                        {Object.values(attendanceStats.byCat).map(c => `${c.name}: ${c.qty} pl. / ${c.revenue.toFixed(2)} €`).join(' · ')}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span className="badge bg-primary">Places confirmées: {attendanceStats.totalPaid}</span>
                  <span className="badge bg-success">Présents: {attendanceStats.checkedIn}</span>
                  <span className="badge bg-secondary">Toutes réservations: {attendanceStats.totalReservations}</span>
                </div>
                <div className="card border-light">
                  <div className="card-body">
                    <div className="fw-bold mb-2">Check-in / Présence (QR)</div>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: 320 }}
                        placeholder="Scanner ou coller le code / lien QR"
                        value={scanValue}
                        onChange={(e) => setScanValue(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => scanValue.trim() && checkInReservation({ token: scanValue })}
                        disabled={!scanValue.trim()}
                      >
                        Valider
                      </button>
                      {!scannerActive ? (
                        <button type="button" className="btn btn-outline-secondary" onClick={startScanner}>
                          Scanner caméra
                        </button>
                      ) : (
                        <button type="button" className="btn btn-outline-danger" onClick={stopScanner}>
                          Stop scanner
                        </button>
                      )}
                    </div>
                    {checkInBaseUrl && (
                      <div className="mt-3 small text-muted">
                        Lien check-in événement:{' '}
                        <a href={checkInBaseUrl} target="_blank" rel="noreferrer">
                          {checkInBaseUrl}
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ms-2"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(checkInBaseUrl);
                              setCheckInLinkCopied(true);
                              window.setTimeout(() => setCheckInLinkCopied(false), 2000);
                            } catch {
                              setCheckInLinkCopied(false);
                            }
                          }}
                        >
                          Copier
                        </button>
                        {checkInLinkCopied && <span className="text-success ms-2">Copié</span>}
                      </div>
                    )}
                    {scanStatus && <div className="alert alert-success mt-2 mb-0">{scanStatus}</div>}
                    {scanError && <div className="alert alert-warning mt-2 mb-0">{scanError}</div>}
                    {scannerActive && (
                      <div className="mt-3">
                        <video
                          ref={scannerVideoRef}
                          style={{ width: '100%', maxWidth: 320, borderRadius: 8, border: '1px solid #e2e8f0' }}
                        />
                        <div className="small text-muted mt-1">Présentez un QR code devant la caméra.</div>
                      </div>
                    )}
                  </div>
                </div>
                <ReservationCalendar
                  selectedDate={selectedReservationDate}
                  bookedDates={bookedDates}
                  onSelect={(dateKey) => setSelectedReservationDate(dateKey)}
                />
                <div className="card border-light">
                  <div className="card-body">
                    <div className="fw-bold mb-3">
                      {selectedReservationDate
                        ? `Réservations du ${selectedReservationDate}`
                        : 'Sélectionnez une date'}
                    </div>

                    {selectedReservationDate && (reservationsByDate[selectedReservationDate] || []).length === 0 && (
                      <div className="text-muted">Aucune réservation pour cette date.</div>
                    )}

                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {(reservationsByDate[selectedReservationDate || ''] || []).map((reservation) => {
                        const details = reservation.bookingDetails || {};
                        const start = reservation.occurrence?.startsAt ? new Date(reservation.occurrence.startsAt) : null;
                        const end = reservation.occurrence?.endsAt ? new Date(reservation.occurrence.endsAt) : null;
                        const timeLabel = start && end
                          ? `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                          : 'Créneau inconnu';
                        const name = details.firstName || details.lastName
                          ? `${details.firstName || ''} ${details.lastName || ''}`.trim()
                          : (reservation.name || 'Client');
                        const email = details.email || reservation.email;
                        const phone = details.phone || reservation.phone;
                        const address = [details.address, details.postalCode, details.city, details.country]
                          .filter(Boolean)
                          .join(', ');
                        const paymentMethod = details.paymentMethod || reservation.paymentMethod || '—';
                        const transactionId = details.transactionId || reservation.stripePaymentId || reservation.stripeSessionId;
                        const statusLabel = reservation.status || '—';
                        const statusClass = reservation.status === 'PAID'
                          ? 'bg-success'
                          : reservation.status === 'PENDING'
                          ? 'bg-warning text-dark'
                          : reservation.status === 'WAITLIST'
                          ? 'bg-info text-dark'
                          : reservation.status === 'REFUNDED'
                          ? 'bg-secondary'
                          : 'bg-secondary';
                        const canCancel = ['PAID', 'PENDING', 'WAITLIST'].includes(reservation.status);
                        const checkInToken = reservation.checkInToken;
                        const checkedInAt = reservation.checkedInAt ? new Date(reservation.checkedInAt) : null;
                        const checkedInLabel = checkedInAt
                          ? checkedInAt.toLocaleString('fr-FR')
                          : null;
                        const checkInBadgeClass = checkedInAt ? 'bg-success' : 'bg-secondary';
                        const checkInBadgeLabel = checkedInAt ? 'Présent' : 'Non présent';
                        const canCheckIn = reservation.status === 'PAID' && !checkedInAt;
                        const isQrOpen = !!qrExpanded[String(reservation.id)];
                        const checkInUrl = checkInToken && local.slug
                          ? buildCheckInUrl(local.slug, checkInToken)
                          : '';

                        return (
                          <div key={reservation.id} className="border rounded p-3 mb-2 bg-light">
                            <div className="d-flex justify-content-between flex-wrap gap-2">
                              <div>
                                <div className="fw-bold">{timeLabel}</div>
                                <div className="text-muted small">{name}</div>
                              </div>
                              <div className="text-end">
                                <span className={`badge ${statusClass}`}>{statusLabel}</span>
                                <span className={`badge ${checkInBadgeClass} ms-2`}>{checkInBadgeLabel}</span>
                                <div className="small mt-1">
                                  {reservation.qty} place{reservation.qty > 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 small">
                              {email && <div>Email: {email}</div>}
                              {phone && <div>Téléphone: {phone}</div>}
                              {address && <div>Adresse: {address}</div>}
                              <div>Paiement: {paymentMethod}</div>
                              {transactionId && <div>Transaction: {transactionId}</div>}
                              {checkedInLabel && <div>Présent le: {checkedInLabel}</div>}
                              {checkInToken && (
                                <div>
                                  Code présence: <code>{checkInToken}</code>
                                </div>
                              )}
                            </div>
                            <div className="mt-2 d-flex justify-content-between flex-wrap gap-2">
                              <div className="d-flex gap-2 flex-wrap">
                                {checkInToken && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() =>
                                      setQrExpanded((prev) => ({
                                        ...prev,
                                        [String(reservation.id)]: !prev[String(reservation.id)]
                                      }))
                                    }
                                  >
                                    {isQrOpen ? 'Masquer QR' : 'Afficher QR'}
                                  </button>
                                )}
                                {canCheckIn && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={async () => {
                                      setCheckInLoadingId(reservation.id);
                                      await checkInReservation({ reservationId: reservation.id });
                                      setCheckInLoadingId(null);
                                    }}
                                    disabled={checkInLoadingId === reservation.id}
                                  >
                                    {checkInLoadingId === reservation.id ? '...' : 'Marquer présent'}
                                  </button>
                                )}
                              </div>
                              {canCancel && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => cancelReservation(reservation.id)}
                                  disabled={cancellingId === reservation.id}
                                >
                                  {cancellingId === reservation.id ? 'Annulation...' : 'Annuler'}
                                </button>
                              )}
                            </div>
                            {isQrOpen && checkInToken && (
                              <div className="mt-3">
                                <QrCodeImage value={checkInUrl || checkInToken} size={140} />
                                {checkInUrl && (
                                  <div className="small text-muted mt-2">
                                    {checkInUrl}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Onglet Paiement */}
        {tab === 'payment' && (
          <div className="animate-fade-in" style={{ display: 'grid', gap: 16 }}>
            <h6 className="border-bottom pb-2 mb-1 text-primary">Méthodes de paiement</h6>

            {local.priceCents === 0 && (
              <div className="alert alert-info py-2 mb-0">
                Événement gratuit — le paiement ne s'applique pas.
              </div>
            )}

            {/* Stripe */}
            <div className="card border-light">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">💳 Stripe (carte bancaire)</div>
                    <div className="small text-muted mt-1">
                      {isLoading ? 'Vérification...' : hasStripeKey
                        ? '✅ Clé Stripe configurée dans Admin → Clés API'
                        : '⚠️ Aucune clé Stripe — configurez-la dans Admin → Clés API'}
                    </div>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      style={{ width: 42, height: 22 }}
                      checked={local.enableStripe !== false}
                      disabled={!hasStripeKey}
                      onChange={e => update({ enableStripe: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* PayPal */}
            <div className="card border-light">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">🅿️ PayPal</div>
                    <div className="small text-muted mt-1">
                      {isLoading ? 'Vérification...' : hasPaypalKey
                        ? '✅ Client ID PayPal configuré dans Admin → Clés API'
                        : '⚠️ Aucun Client ID PayPal — configurez-le dans Admin → Clés API'}
                    </div>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      style={{ width: 42, height: 22 }}
                      checked={local.enablePaypal !== false}
                      disabled={!hasPaypalKey}
                      onChange={e => update({ enablePaypal: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cash / sur place */}
            <div className="card border-light">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">💵 Paiement sur place (cash)</div>
                    <div className="small text-muted mt-1">
                      La réservation est enregistrée en attente — le paiement est encaissé physiquement.
                    </div>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      style={{ width: 42, height: 22 }}
                      checked={!!local.enableCash}
                      onChange={e => update({ enableCash: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {!hasStripeKey && !hasPaypalKey && !local.enableCash && local.priceCents > 0 && (
              <div className="alert alert-warning">
                ⚠️ Aucun moyen de paiement actif. Les visiteurs ne pourront pas finaliser leur réservation.
              </div>
            )}
          </div>
        )}

        {/* Onglet Adresse */}
        {tab === 'address' && (
          <div style={{ display: 'grid', gap: 12 }}>
            {/* Détection clé Google Maps */}
            <div>
              <label style={{ fontWeight: 600 }}>Google Maps</label>
              {gmLoading ? (
                <div className="text-muted small">Vérification de la clé Google Maps…</div>
              ) : hasGMapKey ? (
                <div className="alert alert-success py-2 my-2">
                  ✅ Clé Google Maps détectée. Vous pouvez afficher une carte dans le rendu HTML.
                </div>
              ) : (
                <div className="alert alert-warning py-2 my-2">
                  ⚠️ Aucune clé Google Maps détectée. L’embed reste possible, mais la carte avancée peut être limitée.
                </div>
              )}
              <div className="form-check">
                <input
                  id="enableGeo"
                  className="form-check-input"
                  type="checkbox"
                  checked={!!local.enableGeolocation}
                  onChange={e => update({ enableGeolocation: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="enableGeo">
                  Afficher une carte (géolocalisation) dans le rendu HTML
                </label>
              </div>
            </div>

            <input
              type="text"
              className="form-control"
              placeholder="Adresse"
              value={local.addressLine1 || ''}
              onChange={e => update({ addressLine1: e.target.value })}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-control"
                style={{ flex: 2, minWidth: 140 }}
                placeholder="Ville"
                value={local.city || ''}
                onChange={e => update({ city: e.target.value })}
              />
              <input
                type="text"
                className="form-control"
                style={{ flex: 1, minWidth: 90 }}
                placeholder="CP"
                value={local.postalCode || ''}
                onChange={e => update({ postalCode: e.target.value })}
              />
              <div style={{ flex: 1, minWidth: 160 }}>
                <CountrySelect
                  value={local.country || ''}
                  onChange={(e) => update({ country: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'paypal' && (
          <PayPalOrdersPanel pluginType="event" shopId={currentConfig.id} />
        )}
        {tab === 'stripe' && (
          <StripeOrdersPanel pluginType="event" shopId={currentConfig.id} />
        )}

      </div>
    </div>
  );
};
